// Normalises every event shape the agents service produces (live SSE stream
// AND replayed history) into a flat list of UI rows.
//
// Row shape:
//   { kind: 'human' | 'ai' | 'tool', content, reasoning?, toolName?,
//     messageId?, raw, seq?, attachments? }
//
// As of the runtime split the backend emits a small canonical event set
// (see streams/normalize.js):
//   user_prompt           { prompt, attachments }
//   ai_delta              { id, text }
//   ai_reasoning_delta    { id, text }   // Anthropic extended thinking
//   tool_call             { id, name, args }
//   tool_result           { id, name, result }
//
// We still understand the legacy `{ type: "update", output }` shape so old
// threads keep replaying correctly.
//
// `coalesceRows` merges consecutive AIMessageChunk fragments that share an
// id so a streamed response renders as a single bubble instead of a wall of
// fragments.

const messageKindFromId = (id = "") => {
    if (id.includes("HumanMessage")) return "human";
    if (id.includes("ToolMessage")) return "tool";
    if (id.includes("SystemMessage")) return "system";
    if (id.includes("AIMessage")) return "ai";
    return null;
};

const messageKindFromType = (type = "") => {
    const t = String(type).toLowerCase();
    if (t === "human") return "human";
    if (t === "ai" || t === "aimessagechunk") return "ai";
    if (t === "tool") return "tool";
    if (t === "system") return "system";
    return null;
};

const idString = (msg) => Array.isArray(msg.id) ? msg.id.join(".") : (msg.id || "");

// True for any langchain-style serialised message we can render.
const isMessageLike = (n) => {
    if (!n || typeof n !== "object" || Array.isArray(n)) return false;
    if (Array.isArray(n.id) && n.id.some((x) => typeof x === "string" && x.endsWith("Message"))) return true;
    if (Array.isArray(n.id) && n.id.some((x) => typeof x === "string" && x.endsWith("MessageChunk"))) return true;
    if (typeof n.type === "string" && messageKindFromType(n.type)
        && (n.content !== undefined || n.kwargs?.content !== undefined)) return true;
    if (n.kwargs && typeof n.kwargs === "object" && "content" in n.kwargs && (n.lc || n.type === "constructor")) return true;
    return false;
};

const extractContent = (msg) => {
    const content = msg?.kwargs?.content ?? msg?.content;
    if (content == null) return "";
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
        return content
            .map((b) => {
                if (typeof b === "string") return b;
                if (!b || typeof b !== "object") return "";
                // Drop reasoning / tool_use blocks from rendered text.
                if (b.type === "thinking" || b.type === "reasoning"
                    || b.type === "reasoning_content" || b.type === "tool_use"
                    || b.type === "tool_result") return "";
                return b.text || "";
            })
            .filter(Boolean)
            .join("");
    }
    return String(content);
};

// Extract reasoning ("extended thinking") from a langchain-serialised AI
// message. Mirrors the runtime normalizer so legacy `{ type: "update" }`
// payloads still surface thoughts after a reload.
const extractReasoning = (msg) => {
    const content = msg?.kwargs?.content ?? msg?.content;
    if (!Array.isArray(content)) return "";
    let out = "";
    for (const b of content) {
        if (!b || typeof b !== "object") continue;
        const t = b.type;
        if (t !== "thinking" && t !== "reasoning" && t !== "reasoning_content") continue;
        if (typeof b.thinking === "string") { out += b.thinking; continue; }
        if (typeof b.reasoning === "string") { out += b.reasoning; continue; }
        if (typeof b.text === "string") { out += b.text; continue; }
        const inner = b.reasoning_content || b.reasoningContent;
        if (inner && typeof inner === "object" && typeof inner.text === "string") {
            out += inner.text;
        }
    }
    return out;
};

const rowFromMessage = (msg) => {
    const idStr = idString(msg);
    const kind = messageKindFromId(idStr) || messageKindFromType(msg.type) || "ai";
    if (kind === "system") return null;
    const content = extractContent(msg);
    const reasoning = kind === "ai" ? extractReasoning(msg) : "";
    const toolName = msg?.kwargs?.name || msg?.name;
    if (!content && !reasoning && kind !== "tool") return null;
    const messageId = msg?.kwargs?.id || msg?.id_field || msg?.run_id || null;
    return { kind, content, reasoning, toolName, messageId, raw: msg };
};

// Walk an arbitrary stream payload and emit rows. Used only for the legacy
// `{ type: "update", output }` event shape.
const rowsFromOutput = (output) => {
    const rows = [];
    const visit = (node) => {
        if (node == null) return;
        if (isMessageLike(node)) {
            const r = rowFromMessage(node);
            if (r) rows.push(r);
            return;
        }
        if (Array.isArray(node)) {
            if (node.length === 2 && isMessageLike(node[0])) {
                const r = rowFromMessage(node[0]);
                if (r) rows.push(r);
                return;
            }
            for (const item of node) visit(item);
            return;
        }
        if (typeof node !== "object") return;
        if (Array.isArray(node.messages)) {
            for (const m of node.messages) {
                if (isMessageLike(m)) {
                    const r = rowFromMessage(m);
                    if (r) rows.push(r);
                }
            }
            return;
        }
        for (const key of Object.keys(node)) {
            if (key === "raw") continue;
            visit(node[key]);
        }
    };
    visit(output);
    return rows;
};

const formatToolArgs = (args) => {
    if (args == null) return "";
    if (typeof args === "string") return args;
    try { return JSON.stringify(args, null, 2); }
    catch { return String(args); }
};

const rowsFromStreamEvent = (event, seq) => {
    if (!event || typeof event !== "object") return [];

    if (event.type === "user_prompt") {
        const hasContent = !!event.prompt || (event.attachments && event.attachments.length);
        if (!hasContent) return [];
        return [{
            kind: "human",
            content: event.prompt || "",
            attachments: event.attachments || undefined,
            raw: event,
            seq,
        }];
    }

    if (event.type === "ai_delta") {
        if (!event.text) return [];
        return [{
            kind: "ai",
            content: event.text,
            messageId: event.id,
            raw: event,
            seq,
        }];
    }

    // Extended-thinking deltas (Anthropic Claude on Bedrock Converse, etc.).
    // Streamed live alongside `ai_delta`; we keep them in a parallel
    // `reasoning` field on the same AI row so the ThinkingPanel can render
    // them above the visible reply.
    if (event.type === "ai_reasoning_delta") {
        if (!event.text) return [];
        return [{
            kind: "ai",
            content: "",
            reasoning: event.text,
            messageId: event.id,
            raw: event,
            seq,
        }];
    }

    // Persisted full AI message (one per assistant turn). Stored at the end
    // of streaming so reloading a thread doesn't have to coalesce hundreds
    // of token-deltas. Live streams keep emitting `ai_delta` events.
    if (event.type === "ai_message") {
        return [{
            kind: "ai",
            content: event.text || "",
            reasoning: event.reasoning || "",
            messageId: event.id,
            raw: event,
            seq,
        }];
    }

    if (event.type === "tool_call") {
        return [{
            kind: "tool_call",
            content: formatToolArgs(event.args),
            toolName: event.name,
            messageId: `tc:${event.id}`,
            raw: event,
            seq,
        }];
    }

    if (event.type === "tool_result") {
        return [{
            kind: "tool",
            content: event.result || "",
            toolName: event.name,
            messageId: `tr:${event.id}`,
            raw: event,
            seq,
        }];
    }

    // Human-in-the-loop pause. The UI renders an interactive approval card.
    if (event.type === "interrupt") {
        return [{
            kind: "interrupt",
            interruptId: event.id,
            payload: event.payload,
            messageId: `intr:${event.id}`,
            raw: event,
            seq,
        }];
    }

    // A run that failed. The backend persists this to the stream updates AND
    // streams it live, so surfacing it as a row (rather than an ephemeral
    // banner) makes the failure visible both while streaming and after a
    // reload / when returning to a thread that errored in the background.
    if (event.type === "error") {
        if (!event.message) return [];
        return [{
            kind: "error",
            content: event.message,
            raw: event,
            seq,
        }];
    }

    // The operator's decision for a previously-emitted interrupt. We don't
    // render a dedicated row for it — instead `resolveInterrupts` flips the
    // matching interrupt row to a "resolved" state.
    if (event.type === "interrupt_resume") {
        return [{
            kind: "interrupt_resume",
            interruptId: event.interruptId || null,
            decision: event.decision || null,
            raw: event,
            seq,
        }];
    }

    // Operator smiley on a claim/auth create. Overlayed onto a synthetic
    // rating row keyed by the tool-result id (`targetId`).
    if (event.type === "rating") {
        if (!event.targetId || event.score == null) return [];
        return [{
            kind: "rating_saved",
            targetId: event.targetId,
            score: event.score,
            toolName: event.toolName,
            at: event.at,
            raw: event,
            seq,
        }];
    }

    // Legacy: pre-normalizer events stored on disk.
    if (event.type === "update" && event.output != null) {
        return rowsFromOutput(event.output).map((r) => ({ ...r, seq }));
    }

    return [];
};

// Merge adjacent AI deltas that share a messageId into a single row. Tool
// rows are never merged so each tool result/call shows up distinctly.
// Reasoning deltas (extended thinking) merge into the same row's
// `reasoning` field, sitting beside the visible `content`.
export const coalesceRows = (rows) => {
    const out = [];
    for (const row of rows) {
        const last = out[out.length - 1];
        if (
            last
            && last.kind === "ai"
            && row.kind === "ai"
            && last.messageId
            && last.messageId === row.messageId
        ) {
            last.content = (last.content || "") + (row.content || "");
            if (row.reasoning) {
                last.reasoning = (last.reasoning || "") + row.reasoning;
            }
            continue;
        }
        out.push({ ...row });
    }
    return out;
};

// Hot path for live SSE: append one (or a few) newly-parsed rows onto an
// existing list. When the event is a single AI/reasoning delta that belongs
// to the trailing AI bubble, we splice that bubble in O(1) instead of
// re-walking the whole transcript through coalesceRows — which used to run
// on every token and was a major source of streaming jank.
export const appendStreamRows = (existing, incoming) => {
    if (!incoming?.length) return existing;
    if (
        incoming.length === 1
        && incoming[0].kind === "ai"
        && existing.length > 0
    ) {
        const last = existing[existing.length - 1];
        const row = incoming[0];
        if (
            last.kind === "ai"
            && last.messageId
            && last.messageId === row.messageId
        ) {
            const next = {
                ...last,
                content: (last.content || "") + (row.content || ""),
            };
            if (row.reasoning) {
                next.reasoning = (last.reasoning || "") + row.reasoning;
            }
            const out = existing.slice();
            out[out.length - 1] = next;
            return out;
        }
    }
    return resolveInterrupts(coalesceRows([...existing, ...incoming]));
};

// Walks the row list and marks any `interrupt` whose id was later resolved
// by an `interrupt_resume` row. We then drop the `interrupt_resume` rows
// since they don't render on their own — the resolved interrupt card shows
// the decision inline.
export const resolveInterrupts = (rows) => {
    const decisions = new Map();
    for (const r of rows) {
        if (r.kind === "interrupt_resume" && r.interruptId) {
            decisions.set(r.interruptId, r.decision || { approved: false });
        }
    }
    const out = [];
    for (const r of rows) {
        if (r.kind === "interrupt_resume") continue;
        if (r.kind === "interrupt" && decisions.has(r.interruptId)) {
            out.push({ ...r, resolved: true, decision: decisions.get(r.interruptId) });
        } else {
            out.push(r);
        }
    }
    return out;
};

// Live SSE: parsed `data:` payload.
export const rowsFromStreamUpdate = (event) => rowsFromStreamEvent(event, undefined);

const CLAIM_SUBMIT_TOOLS = new Set([
    "submit_professional_claim",
    "submit_institutional_claim",
    "submit_professional_claims_batch",
    "submit_institutional_claims_batch",
    "submit_professional_repricing_claim",
    "submit_institutional_repricing_claim",
    "submit_professional_repricing_claims_batch",
    "submit_institutional_repricing_claims_batch",
]);

const AUTH_SUBMIT_TOOLS = new Set(["submit_authorization"]);

const parseToolJson = (content) => {
    if (content && typeof content === "object") return content;
    if (!content || typeof content !== "string") return null;
    try {
        return JSON.parse(content);
    } catch {
        return null;
    }
};

const authLooksApproved = (parsed) => {
    if (parsed.needsRepair === true) return false;
    const statuses = [];
    const push = (s) => {
        if (s != null && s !== "") statuses.push(String(s).toUpperCase());
    };
    push(parsed.status);
    push(parsed.businessStatus);
    push(parsed.data?.status);
    push(parsed.data?.authorizationStatus);
    const items = Array.isArray(parsed.data)
        ? parsed.data
        : Array.isArray(parsed.data?.items) ? parsed.data.items
            : Array.isArray(parsed.data?.data) ? parsed.data.data
                : [];
    for (const item of items) {
        push(item?.status);
        push(item?.authorizationStatus);
        if (item?.transactionInformation?.needsRepair === true
            || item?.needsRepair === true) {
            return false;
        }
    }
    if (statuses.length && statuses.every((s) => s === "ERROR" || s === "FAILURE")) return false;
    if (statuses.some((s) => s === "ERROR" || s === "FAILURE")
        && !statuses.some((s) => s === "SUCCESS" || s === "APPROVED")) {
        return false;
    }
    return true;
};

export const isRateableOutcome = (toolName, content) => {
    const text = typeof content === "string"
        ? content
        : (content && typeof content === "object" ? JSON.stringify(content) : "");
    if (AUTH_SUBMIT_TOOLS.has(toolName)) {
        const parsed = parseToolJson(content);
        if (parsed) {
            if (parsed.ok !== true) return false;
            return authLooksApproved(parsed);
        }
        if (!/\bok:\s*true\b/.test(text)) return false;
        if (/status:\s*SUCCESS\b/i.test(text)) return true;
        if (/status:\s*(ERROR|FAILURE)\b/i.test(text)) return false;
        if (/needsRepair:\s*true/i.test(text)) return false;
        return false;
    }
    if (!CLAIM_SUBMIT_TOOLS.has(toolName)) return false;
    const parsed = parseToolJson(content);
    if (parsed) {
        if (parsed.ok !== true) return false;
        if (parsed.totals && Number(parsed.totals.succeeded) === 0) return false;
        return true;
    }
    return /\bok:\s*true\b/.test(text) && !/\bok:\s*false\b/.test(text);
};

export const ratingPromptForTool = (toolName) => {
    if (AUTH_SUBMIT_TOOLS.has(toolName)) return "How did this authorization go?";
    if (String(toolName || "").includes("batch")) return "How did these claims go?";
    return "How did this claim go?";
};

// Insert a smiley-rating row after each successful claim/auth submit, and
// overlay any persisted `rating_saved` events (or scores from a prior
// attachRatings pass) onto those rows.
export const attachRatings = (rows) => {
    const scores = new Map();
    const stripped = [];
    for (const r of rows) {
        if (r.kind === "rating_saved" && r.targetId) {
            scores.set(r.targetId, { score: r.score, at: r.at });
            continue;
        }
        if (r.kind === "rating") {
            if (r.targetId && r.score != null) {
                scores.set(r.targetId, { score: r.score, at: r.at });
            }
            continue;
        }
        stripped.push(r);
    }
    const out = [];
    for (const r of stripped) {
        out.push(r);
        if (r.kind === "tool" && r.messageId && isRateableOutcome(r.toolName, r.content)) {
            const saved = scores.get(r.messageId);
            out.push({
                kind: "rating",
                targetId: r.messageId,
                toolName: r.toolName,
                score: saved?.score ?? null,
                at: saved?.at ?? null,
                prompt: ratingPromptForTool(r.toolName),
                messageId: `rate:${r.messageId}`,
            });
        }
    }
    return out;
};

export const finalizeRows = (rows) => attachRatings(resolveInterrupts(coalesceRows(rows)));

// Reload: array of `{seq, data}` rows from GET /threads/:id and /updates.
export const rowsFromPersistedUpdates = (updates = []) => {
    const rows = [];
    for (const u of updates) rows.push(...rowsFromStreamEvent(u.data, u.seq));
    return finalizeRows(rows);
};
